"use client";

import { ShortCode } from "@repo/design-system-2/components/brands/utility/shortCode";
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
} from "@repo/design-system-2/components/ui/combobox";
import { Spinner } from "@repo/design-system-2/components/ui/spinner";
import { useDebounce } from "@repo/design-system-2/hooks/use-debounce";
import { cn } from "@repo/design-system-2/lib/utils";
import { UTCDate } from "@date-fns/utc";
import { format } from "date-fns";
import Image from "next/image";
import { useAction } from "next-safe-action/hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ManageSearchAccess } from "@/lib/auth/manage-search-access";
import { searchArchiveItems, searchBackgrounds, searchEvents, searchRegions, searchSchools, searchVendors } from "./action-search";
import { searchSeries } from "./action-series";

type SearchResult = {
    id: string;
    label: string;
    description?: string;
    imageUrl?: string | null;
    shortCode?: string | null;
};

type EntitySearchProps = {
    value: SearchResult | null;
    onChange: (value: SearchResult | null) => void;
    placeholder?: string;
    mode: "vendor" | "series" | "background" | "event" | "exam" | "school" | "region" | "feisVendor" | "album" | "artist";
    disabled?: boolean;
    /** When searching events, exclude this event and resolve to each series' latest event. */
    excludeEventId?: string;
    /** Entity the user is managing; omit only on admin-only flows such as create dialogs. */
    accessContext?: ManageSearchAccess;
};

function searchAccessInput(accessContext?: ManageSearchAccess) {
    if (!accessContext) {
        return {};
    }

    return {
        accessArea: accessContext.area,
        accessObjectId: accessContext.objectId,
    };
}

function formatEventDate(date: Date | string) {
    return format(new UTCDate(date), "MMM d, yyyy");
}

function EntityThumbnail({ className, item }: { className?: string; item: SearchResult }) {
    if (item.imageUrl) {
        return (
            <Image
                alt=""
                className={cn("size-8 shrink-0 rounded-full object-cover", className)}
                height={32}
                src={item.imageUrl}
                width={32}
            />
        );
    }

    if (item.shortCode) {
        return <ShortCode className={cn("size-8 shrink-0", className)} title={item.shortCode} />;
    }

    return null;
}

function EntityOptionContent({ item }: { item: SearchResult }) {
    const hasVisual = Boolean(item.imageUrl || item.shortCode);

    return (
        <div className="flex min-w-0 items-center gap-2">
            {hasVisual && <EntityThumbnail item={item} />}
            <div className="flex min-w-0 flex-col">
                <span className="truncate">{item.label}</span>
                {item.description && (
                    <span className="text-muted-foreground text-xs">{item.description}</span>
                )}
            </div>
        </div>
    );
}

export function EntitySearch({ value, onChange, placeholder, mode, disabled, excludeEventId, accessContext }: EntitySearchProps) {
    const [query, setQuery] = useState(value?.label ?? "");
    const [selectedItem, setSelectedItem] = useState<SearchResult | null>(value);
    const [open, setOpen] = useState(false);
    const debouncedQuery = useDebounce(query, 300);
    const [results, setResults] = useState<SearchResult[]>(() => (value ? [value] : []));

    useEffect(() => {
        if (!value) {
            return;
        }

        setSelectedItem(value);
        setResults((current) => (current.some((item) => item.id === value.id) ? current : [value, ...current]));

        if (!open) {
            setQuery(value.label);
        }
    }, [open, value?.id, value?.label, value]);

    const vendorSearch = useAction(searchVendors, {
        onSuccess({ data }) {
            if (data) {
                setResults(
                    data
                        .filter((item) => {
                            if (mode === "feisVendor") {
                                return item.providesFeisServices === true;
                            }
                            return true;
                        })
                        .map((item) => ({ id: item.id, label: item.label }))
                );
            }
        },
    });

    const seriesSearch = useAction(searchSeries, {
        onSuccess({ data }) {
            if (Array.isArray(data)) {
                setResults(
                    data.map((item) => ({
                        id: item.id,
                        label: item.label,
                        shortCode: item.shortCode,
                    }))
                );
            }
        },
    });

    const backgroundSearch = useAction(searchBackgrounds, {
        onSuccess({ data }) {
            if (data) {
                setResults(data.map((item) => ({ id: item.id, label: item.label ?? "Untitled" })));
            }
        },
    });

    const regionSearch = useAction(searchRegions, {
        onSuccess({ data }) {
            if (Array.isArray(data)) {
                setResults(
                    data.map((item) => ({
                        id: item.id,
                        label: item.label,
                        shortCode: item.shortCode,
                    }))
                );
            }
        },
    });

    const archiveSearch = useAction(searchArchiveItems, {
        onSuccess({ data }) {
            if (data) {
                setResults(
                    data.map((item) => ({
                        id: item.itemId,
                        imageUrl: item.imageUrl,
                        label: item.label,
                        shortCode: item.shortCode,
                    }))
                );
            }
        },
    });

    const eventSearch = useAction(searchEvents, {
        onSuccess({ data }) {
            if (Array.isArray(data)) {
                setResults(
                    data.map((item) => ({
                        description: item.firstDate ? formatEventDate(item.firstDate) : undefined,
                        id: item.id,
                        label: item.label,
                        shortCode: item.shortCode,
                    }))
                );
            }
        },
    });

    const schoolSearch = useAction(searchSchools, {
        onSuccess({ data }) {
            if (Array.isArray(data)) {
                setResults(
                    data.map((item) => ({
                        id: item.id,
                        imageUrl: item.imageUrl,
                        label: item.label,
                        shortCode: item.shortCode,
                    }))
                );
            }
        },
    });

    const isPending =
        vendorSearch.isExecuting ||
        seriesSearch.isExecuting ||
        backgroundSearch.isExecuting ||
        regionSearch.isExecuting ||
        archiveSearch.isExecuting ||
        eventSearch.isExecuting ||
        schoolSearch.isExecuting;

    useEffect(() => {
        const access = searchAccessInput(accessContext);

        if (mode === "vendor" || mode === "feisVendor") {
            vendorSearch.execute({
                ...access,
                feisServicesOnly: mode === "feisVendor",
                query: debouncedQuery,
            });
        } else if (mode === "series") {
            seriesSearch.execute({ query: debouncedQuery });
        } else if (mode === "background") {
            backgroundSearch.execute({ ...access, query: debouncedQuery });
        } else if (mode === "region") {
            regionSearch.execute({ ...access, query: debouncedQuery });
        } else if (mode === "event") {
            eventSearch.execute({
                ...access,
                excludeEventId,
                query: debouncedQuery,
            });
        } else if (mode === "school") {
            schoolSearch.execute({ ...access, query: debouncedQuery });
        } else {
            archiveSearch.execute({
                ...access,
                itemType: mode,
                query: debouncedQuery,
            });
        }
    }, [
        accessContext,
        debouncedQuery,
        excludeEventId,
        mode,
        archiveSearch.execute,
        backgroundSearch.execute,
        eventSearch.execute,
        regionSearch.execute,
        schoolSearch.execute,
        seriesSearch.execute,
        vendorSearch.execute,
    ]);

    const items = useMemo(() => {
        const active = selectedItem ?? value;

        if (!active) {
            return results;
        }

        if (results.some((item) => item.id === active.id)) {
            return results;
        }

        return [active, ...results];
    }, [results, selectedItem, value]);

    const selectedValue = useMemo(() => {
        const active = selectedItem ?? value;

        if (!active) {
            return null;
        }

        return items.find((item) => item.id === active.id) ?? null;
    }, [items, selectedItem, value]);

    const handleValueChange = useCallback(
        (item: SearchResult | null) => {
            setSelectedItem(item);
            onChange(item);
            setQuery(item?.label ?? "");
        },
        [onChange]
    );

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            setOpen(isOpen);

            if (!isOpen) {
                setQuery(selectedItem?.label ?? "");
            }
        },
        [selectedItem]
    );

    const getItemLabel = useCallback((item: SearchResult) => item.label, []);

    return (
        <div className="relative w-full">
            <Combobox
                disabled={disabled || isPending}
                inputValue={open ? query : (selectedItem?.label ?? query)}
                items={items}
                itemToStringLabel={getItemLabel}
                onInputValueChange={setQuery}
                onOpenChange={handleOpenChange}
                onValueChange={handleValueChange}
                open={open}
                value={selectedValue}
            >
                <ComboboxInput
                    className="w-full"
                    placeholder={placeholder ?? "Search..."}
                    showClear={Boolean(selectedItem)}
                />
                <ComboboxContent>
                    <ComboboxEmpty>No results found.</ComboboxEmpty>
                    <ComboboxList>
                        {(item) => (
                            <ComboboxItem key={item.id} value={item}>
                                <EntityOptionContent item={item} />
                            </ComboboxItem>
                        )}
                    </ComboboxList>
                </ComboboxContent>
            </Combobox>
            {isPending && <Spinner className="pointer-events-none absolute top-1/2 right-10 size-4 -translate-y-1/2" />}
        </div>
    );
}
